import React from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { OrgCard } from "@/components/OrgCard";
import { SectionHeading, RequestInfoButton } from "@/components/common";
import { useEnquiry } from "@/context/EnquiryContext";
import { useSEO } from "@/lib/useSEO";
import { ORGANIZATIONS } from "@/config/site";
import {
  ArrowRight, Cpu, GraduationCap, HeartHandshake, Brain, Sparkles,
  Rocket, Lightbulb, Users, TrendingUp, Repeat, Layers, ArrowUpRight, ChevronRight,
} from "lucide-react";

const heroImg = "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const foundationImg = "https://images.unsplash.com/photo-1686624386665-4cd01b96d0f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const aboutImg = "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const whatWeDo = [
  { icon: Cpu, title: "Technology", desc: "Digital solutions, IT services, automation and technology transformation." },
  { icon: Brain, title: "AI & Future Skills", desc: "Practical learning in Artificial Intelligence, DevOps, Cloud, Python, automation and emerging technologies." },
  { icon: GraduationCap, title: "Education", desc: "Academic and professional pathways designed to support learning and career development." },
  { icon: HeartHandshake, title: "Social Impact", desc: "Community-focused initiatives around education, skills, awareness and empowerment." },
];

const whyFeatures = [
  { icon: Cpu, title: "Technology-Driven Approach" },
  { icon: Layers, title: "Practical Learning" },
  { icon: TrendingUp, title: "Industry Orientation" },
  { icon: Rocket, title: "Career Development" },
  { icon: Lightbulb, title: "Innovation" },
  { icon: Users, title: "Community Impact" },
  { icon: Repeat, title: "Continuous Learning" },
  { icon: Sparkles, title: "Future Skills" },
];

export default function Home() {
  const { openEnquiry } = useEnquiry();
  useSEO({
    title: "Atmasamman Group | ATS, ASAI, AIMT & Foundation",
    description: "Explore Atmasamman Group and its initiatives across technology services, artificial intelligence and future skills, education, and social impact through ATS, ASAI, AIMT and Atmasamman Foundation.",
    path: "/",
  });

  return (
    <Layout>
      {/* HERO */}
      <section className="grad-navy relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="circuit-grid absolute inset-0 opacity-70" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-24 top-10 h-96 w-96 rounded-full bg-orange-500/15 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5 font-mono text-xs font-medium uppercase tracking-wider text-orange-400">
              <Sparkles className="h-3.5 w-3.5" /> One Group. Multiple Missions.
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Building Skills.<br />Creating Technology.<br /><span className="grad-orange-text">Transforming Lives.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              Atmasamman Group is an ecosystem focused on technology, artificial intelligence, education, professional development and social impact.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={() => openEnquiry({ interest: "Atmasamman Group" })} data-testid="hero-request-info-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-orange-500/25 transition-all hover:bg-orange-600 hover:-translate-y-0.5 md:text-base">
                REQUEST INFORMATION <ArrowRight className="h-4 w-4" />
              </button>
              <Link to="/organizations" data-testid="hero-explore-orgs-btn"
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white transition-all hover:border-orange-500/50 hover:bg-white/10 md:text-base">
                EXPLORE OUR ORGANIZATIONS
              </Link>
            </div>
            <Link to="/about" data-testid="hero-about-link" className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-white/60 hover:text-orange-400">
              About Atmasamman Group <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="animate-fade-up relative" style={{ animationDelay: "0.15s" }}>
            <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/40">
              <img src={heroImg} alt="Technology, AI and innovation across the Atmasamman ecosystem" className="h-full w-full object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/70 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-white/10 bg-[#1C2541] px-5 py-4 shadow-xl sm:block">
              <p className="font-mono text-xs uppercase tracking-widest text-orange-400">Ecosystem</p>
              <p className="mt-1 font-display text-lg font-bold text-white">ATS · ASAI · AIMT · Foundation</p>
            </div>
          </div>
        </div>
      </section>

      {/* OUR ECOSYSTEM */}
      <section className="bg-[#F8FAFC] py-16 md:py-24" data-testid="ecosystem-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Our Ecosystem" title="One Group. Multiple Missions. One Vision."
            subtitle="Explore the organizations working together under Atmasamman Group." />
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            {ORGANIZATIONS.map((org) => <OrgCard key={org.code} org={org} detailed />)}
          </div>
        </div>
      </section>

      {/* WHAT WE WORK ON */}
      <section className="py-16 md:py-24" data-testid="what-we-do-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="What We Do" title="What We Work On" />
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whatWeDo.map(({ icon: Icon, title, desc }) => (
              <div key={title} data-testid={`work-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/40 hover:shadow-xl md:p-8">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#0B132B] text-orange-400 transition-colors group-hover:bg-orange-500 group-hover:text-white">
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ATMASAMMAN GROUP */}
      <section className="grad-navy relative overflow-hidden py-16 md:py-24" data-testid="why-section">
        <div className="circuit-grid absolute inset-0 opacity-60" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center dark eyebrow="Why Atmasamman Group" title="Creating Opportunities Through Technology & Education" />
          <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-4">
            {whyFeatures.map(({ icon: Icon, title }) => (
              <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-center backdrop-blur transition-all hover:border-orange-500/40 hover:bg-white/[0.06] md:p-6">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/15 text-orange-400">
                  <Icon className="h-6 w-6" />
                </div>
                <p className="mt-4 font-display text-sm font-semibold text-white md:text-base">{title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-16 md:py-24" data-testid="about-preview-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="relative order-2 lg:order-1">
            <img src={aboutImg} alt="Collaboration across the Atmasamman Group ecosystem" className="w-full rounded-3xl border border-slate-200 object-cover shadow-lg" loading="lazy" />
          </div>
          <div className="order-1 lg:order-2">
            <SectionHeading eyebrow="About the Group" title="Technology. Education. Opportunity." />
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              Atmasamman Group believes technology and education can create sustainable opportunities for individuals, professionals, businesses and communities.
            </p>
            <Link to="/about" data-testid="about-preview-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#0B132B] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1C2541] hover:-translate-y-0.5 md:text-base">
              Learn About Atmasamman Group <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDATION FEATURE */}
      <section className="bg-[#F8FAFC] py-16 md:py-24" data-testid="foundation-feature-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-lg">
            <img src={foundationImg} alt="Atmasamman Foundation community and education initiatives" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/50 to-transparent" />
          </div>
          <div>
            <SectionHeading eyebrow="Atmasamman Foundation" title="Creating Impact Beyond Business" />
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              Through Atmasamman Foundation, the Group extends its mission into education, skill development, technology awareness and community empowerment.
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <Link to="/foundation" data-testid="foundation-feature-cta"
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-orange-600 hover:-translate-y-0.5 md:text-base">
                Explore Atmasamman Foundation <ArrowRight className="h-4 w-4" />
              </Link>
              <RequestInfoButton interest="Atmasamman Foundation" variant="outline" testId="foundation-feature-request" />
            </div>
          </div>
        </div>
      </section>

      {/* CAREERS */}
      <section className="py-16 md:py-24" data-testid="careers-home-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-sm md:p-12">
            <div className="grid items-center gap-8 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <SectionHeading eyebrow="Careers" title="Build Your Career With Atmasamman"
                  subtitle="Explore opportunities across technology, artificial intelligence, education and social-impact initiatives." />
              </div>
              <div className="flex flex-col gap-3">
                <Link to="/careers" data-testid="home-explore-careers"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0B132B] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1C2541] md:text-base">
                  Explore Careers <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/careers" data-testid="home-explore-internships"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-orange-500 px-6 py-3 text-sm font-semibold text-orange-600 transition-all hover:bg-orange-500 hover:text-white md:text-base">
                  Explore Internships
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-[#0B132B] py-16 md:py-24" data-testid="final-cta-section">
        <div className="circuit-grid absolute inset-0 opacity-60" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-orange-500 to-transparent" />
        <div className="pointer-events-none absolute -right-20 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-orange-500/15 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Let's Build Something <span className="grad-orange-text">Meaningful</span> Together.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg">
            Whether you're looking for technology solutions, education, professional development, career opportunities or collaboration, connect with Atmasamman Group.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <button onClick={() => openEnquiry({ interest: "Atmasamman Group" })} data-testid="final-cta-request-info"
              className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-orange-500/25 transition-all hover:bg-orange-600 hover:-translate-y-0.5 md:text-base">
              Request Information <ArrowRight className="h-4 w-4" />
            </button>
            <Link to="/organizations" data-testid="final-cta-explore-orgs"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white transition-all hover:border-orange-500/50 md:text-base">
              Explore Our Organizations <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
